import React from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';

/** `neutral` is for a state that isn't good or bad, such as an archived record. */
export type AppAlertTone = 'neutral' | 'info' | 'success' | 'warning' | 'critical';

interface AppAlertProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  tone?: AppAlertTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onDismiss?: () => void;
  /** Replaces the tone's icon (a BluetoothOff for a printer notice); `false` for none. */
  icon?: React.ReactNode | false;
  /** A button on the right, e.g. Unsnooze or Retry. */
  action?: React.ReactNode;
  /** Tighter padding and small text, for a notice inside a card, composer or menu. */
  compact?: boolean;
  /**
   * Edge to edge: no rounded corners or side borders, a line underneath, one line of small text, for
   * the top of a list or pane (a note about everything below it). Compact's sizes, square.
   */
  bar?: boolean;
}

const toneStyles: Record<
  AppAlertTone,
  { icon: React.ReactNode; className: string }
> = {
  neutral: {
    icon: <Info className="size-4" />,
    className: 'border-border bg-muted text-foreground *:data-[slot=alert-description]:text-muted-foreground',
  },
  info: {
    icon: <Info className="size-4" />,
    className: 'border-info-border bg-info-soft text-info-strong *:data-[slot=alert-description]:text-info-strong',
  },
  success: {
    icon: <CheckCircle2 className="size-4" />,
    className: 'border-success-border bg-success-soft text-success-strong *:data-[slot=alert-description]:text-success-strong',
  },
  warning: {
    icon: <TriangleAlert className="size-4" />,
    className: 'border-warning-border bg-warning-soft text-warning-strong *:data-[slot=alert-description]:text-warning-strong',
  },
  critical: {
    icon: <AlertCircle className="size-4" />,
    className: '',
  },
};

/**
 * Banner-style alert with tones matching former Polaris Banner usage.
 */
export function AppAlert({
  tone = 'info',
  title,
  children,
  className,
  onDismiss,
  icon,
  action,
  compact = false,
  bar = false,
  ...props
}: AppAlertProps) {
  const styles = toneStyles[tone];
  const shownIcon = icon === false ? null : (icon ?? styles.icon);
  const small = compact || bar;
  const side = Boolean(action || onDismiss);

  return (
    <Alert
      variant={tone === 'critical' ? 'destructive' : 'default'}
      className={cn(
        styles.className,
        small && 'px-3 py-2 text-xs has-[>svg]:gap-x-2 [&>svg]:size-3.5 *:data-[slot=alert-description]:text-xs',
        bar && 'rounded-none border-x-0 border-t-0 py-1.5',
        side && 'grid-cols-[0_1fr_auto] has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr_auto]',
        // One line beside a button: centre the icon and text on it.
        side && !(title && children) && 'items-center [&>svg]:translate-y-0',
        className
      )}
      {...props}
    >
      {shownIcon}
      {/* Wraps rather than clamping to one line: a warning must never be cut off. */}
      {title ? <AlertTitle className="line-clamp-none">{title}</AlertTitle> : null}
      {children ? <AlertDescription>{children}</AlertDescription> : null}
      {/* Action and Dismiss share the right-hand column, so neither covers the other or the text. */}
      {side ? (
        <div
          className={cn(
            'col-start-3 row-start-1 flex items-center gap-1 self-center',
            // Beside both lines of a title and text; on one line, its own row so the text centres on it.
            title && children && 'row-span-2',
            small ? 'pl-2' : 'pl-3'
          )}
        >
          {action}
          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-md px-1.5 py-0.5 text-xs text-current/70 hover:bg-black/5 hover:text-current"
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          ) : null}
        </div>
      ) : null}
    </Alert>
  );
}
